// The High priority flag on an account (Deborah, 1 Nov): admin and leadership flag any account, a
// strategist flags their own, everyone else sees it read-only. The server decides
// (`POST /api/accounts/priority`, board/priority.py); this mirrors the rule so the page offers the
// toggle only where the write will be allowed, as `canPushAccount` mirrors the push.
//
// The flag rides on the book row (`high_priority`, joined on serve), so every board, the AE view
// and the sidebar read it from the row. A toggle patches the cached row straight away and puts it
// back if the write fails (`usePriorityToggle.ts`).
//
// Pure, and imported by `node --test`.

import type { BookAccount, BookPayload, Viewer } from '../api/types';
import { sameEmail } from '../sidebar/permissions.ts';

// The three fields the server joins onto a flagged row.
export interface PriorityFields {
  high_priority: boolean;
  high_priority_set_by: string | null;
  high_priority_set_at: string | null;
}

export function isHighPriority(row: Pick<BookAccount, 'high_priority'> | null | undefined): boolean {
  return row?.high_priority === true;
}

export function priorityFieldsOf(row: BookAccount): PriorityFields {
  return {
    high_priority: isHighPriority(row),
    high_priority_set_by: row.high_priority_set_by ?? null,
    high_priority_set_at: row.high_priority_set_at ?? null,
  };
}

type PriorityViewer = Pick<Viewer, 'email' | 'role'> & {
  priority_scope?: 'all' | 'own' | null;
  acting_for?: string | null;
};

// Why this viewer may not change this row's flag, for the disabled toggle's tooltip; null when they may.
export function priorityBlockedReason(viewer: PriorityViewer | null | undefined, row: BookAccount): string | null {
  if (!viewer) return 'Only admins, leadership and the account’s strategist can change this.';

  // No scope at all: an AE, a viewer, or a read-only view-as.
  const scope = viewer.priority_scope ?? null;
  if (!scope) {
    if (viewer.acting_for) return 'Read only while viewing as someone else.';
    return 'Only admins, leadership and the account’s strategist can change this.';
  }
  if (scope === 'all') return null;

  // A strategist: their own rows only, matched on email as the server does.
  if (sameEmail(row.sdr_owner_email, viewer.email)) return null;
  const owner = (row.sdr_owner || '').trim();
  if (!owner) return 'This account has no strategist owner, so only admins and leadership can change this.';
  return `Only ${owner}, admins and leadership can change this.`;
}

// The toggle's label and hover: what it is now, who set it and when, and what a click does.
export function priorityTitle(row: BookAccount, blockedReason: string | null): string {
  const flagged = isHighPriority(row);
  const setBy = row.high_priority_set_by ? row.high_priority_set_by.split('@')[0] : '';
  const setOn = (row.high_priority_set_at ?? '').slice(0, 10);
  const state = flagged
    ? `High priority${setBy ? `, marked by ${setBy}` : ''}${setOn ? ` on ${setOn}` : ''}.`
    : 'Not marked high priority.';
  if (blockedReason) return `${state} ${blockedReason}`;
  return `${state} Click to ${flagged ? 'unmark' : 'mark it high priority'}.`;
}

// The book with one row's flag fields replaced; the same object when the row is not in it.
export function withPriorityFields<Payload extends Pick<BookPayload, 'accounts'>>(
  book: Payload | undefined,
  accountId: string,
  fields: PriorityFields,
): Payload | undefined {
  if (!book) return book;
  const index = book.accounts.findIndex(row => row.id === accountId);
  if (index < 0) return book;
  const accounts = book.accounts.slice();
  accounts[index] = { ...accounts[index]!, ...fields };
  return { ...book, accounts };
}

// What the page says when a change could not be saved and the flag went back.
export function priorityFailureNote(account: string, wanted: boolean, reason: string): string {
  const verb = wanted ? 'mark' : 'unmark';
  return `Could not ${verb} ${account} high priority; it is back as it was. ${reason}`.trim();
}

// The High priority filter: every row when off, the flagged rows when on.
export function keepHighPriority<Row extends BookAccount>(rows: Row[], highPriorityOnly: boolean): Row[] {
  if (!highPriorityOnly) return rows;
  return rows.filter(row => isHighPriority(row));
}

export function highPriorityCount(rows: BookAccount[]): number {
  return rows.filter(row => isHighPriority(row)).length;
}
